import { reactive } from "vue";
import { useEmployeesDirectoryTableViewState } from "@components/EmployeesDirectoryTable/composables/useViewState";
import {
  getEmployeeBadgeCounterProps as getSharedBadgeCounterProps,
  mapDirectoryCoachesToAvatars,
  mapDirectoryMastersToAvatars,
  createSetLoading,
  primeDirectoryTableRouterCache,
  resolveDirectoryTableRouterContext,
} from "@components/DirectoriesShared/directoryTableHelpers.js";
import { getActiveAttachedRolesDisplayProps } from "@components/utils/activeAttachedRolesCount";

let responsibilitiesDirectoryTableViewSingleton = null;

function parseResponsibilityCodeDisplay(codeStr, fallbackName) {
  const s = (codeStr || "").trim();
  if (!s) {
    return { code: "", name: fallbackName || "" };
  }
  const idx = s.indexOf(" - ");
  if (idx === -1) {
    return { code: s, name: fallbackName || "" };
  }
  return {
    code: s.slice(0, idx).trim(),
    name: s.slice(idx + 3).trim() || fallbackName || "",
  };
}

export function useResponsibilitiesDirectoryTableViewState() {
  if (responsibilitiesDirectoryTableViewSingleton) {
    return responsibilitiesDirectoryTableViewSingleton;
  }

  const directoryEmbed = useEmployeesDirectoryTableViewState();

  primeDirectoryTableRouterCache();

  const navigateToResponsibility = (itemOrResId, resTab, filter = null) => {
    const { router } = resolveDirectoryTableRouterContext();
    const isRow =
      itemOrResId &&
      typeof itemOrResId === "object" &&
      Object.prototype.hasOwnProperty.call(itemOrResId, "id");
    const resId = isRow ? itemOrResId.id : itemOrResId;
    if (resId == null || !router) return;
    directoryEmbed.openResponsibilityDirectoryInline(
      resId,
      resTab,
      filter,
      isRow ? itemOrResId : null
    );
  };

  const state = reactive({
    filters: {
      values: [],
      searchText: "",
      showFilterDialog: false,
      clearSelection: "",
    },
    loading: {
      table: false,
    },
    table: {
      currentPage: 1,
      itemsPerPage: 10,
      sortBy: ["name"],
      sortDesc: [false],
      search: "",
    },
  });

  const getOrgObjectCardProps = (item) => {
    const { route } = resolveDirectoryTableRouterContext();
    const { code, name } = parseResponsibilityCodeDisplay(item?.code_str, item?.name);
    const useInline =
      directoryEmbed.state.embedded && !!route?.path?.startsWith("/your-stuff/directories");
    return {
      icon: "responsibility",
      code: code || String(item?.code ?? ""),
      name: name || "",
      showIcon: true,
      showCode: true,
      removeUnderlineToName: true,
      id: item?.id,
      hideHover: false,
      nameLink:
        useInline || !item?.id ? "" : `/organization/responsibilities/${item.id}?resTab=checklist`,
      dynamicWidth: true,
    };
  };

  const getReadinessBarProps = (obj) => {
    const hasAssignments = (obj?.count_assignments ?? 0) > 0;
    const readiness = hasAssignments
      ? Number(obj?.avg_readiness ?? obj?.current_readiness ?? 0)
      : null;
    return {
      recalc: false,
      readiness,
      status: hasAssignments ? "Only Readiness" : "No Assignments",
      noAssignmentsText: "No Assignments",
      enableHover: false,
    };
  };

  const getEmployeeBadgeCounterProps = (item) => getSharedBadgeCounterProps(item);

  const getAttachedToRolesProps = (item) => getActiveAttachedRolesDisplayProps(item);

  const getCoachesAvatarsProps = (item) => ({
    class: "my-4",
    avatars: mapDirectoryCoachesToAvatars(item?.coaches_list),
    maxVisible: 3,
    size: "medium",
    isHoverEnable: true,
    isShowTooltip: true,
    allowDrillDown: true,
  });

  const getMastersAvatarsProps = (item) => ({
    class: "my-4",
    avatars: mapDirectoryMastersToAvatars(item?.masters_list),
    maxVisible: 3,
    size: "medium",
    isHoverEnable: true,
    isShowTooltip: true,
    allowDrillDown: true,
  });

  const applyFilters = (filters) => {
    const normalized = filters
      .map((item) => {
        if (item?.value != null) {
          return typeof item === "string" ? item : item.value;
        }
        return null;
      })
      .filter(Boolean);
    state.filters.values = normalized;
    state.filters.showFilterDialog = false;
  };

  const removeFilters = (removedFilter) => {
    state.filters.clearSelection = removedFilter;
  };

  const clearFilters = () => {
    state.filters.values = [];
    state.filters.showFilterDialog = false;
    state.filters.clearSelection = "";
  };

  responsibilitiesDirectoryTableViewSingleton = {
    state,
    navigateToResponsibility,
    getOrgObjectCardProps,
    getReadinessBarProps,
    getEmployeeBadgeCounterProps,
    getAttachedToRolesProps,
    getCoachesAvatarsProps,
    getMastersAvatarsProps,
    applyFilters,
    removeFilters,
    clearFilters,
    setLoading: createSetLoading(state.loading),
  };

  return responsibilitiesDirectoryTableViewSingleton;
}
